import { useEffect, useState, useCallback } from "react";
import { api, formatEUR } from "@/lib/diningApi";
import { useWaves } from "@/context/DiningWaveContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Send, Link2, Store, Users, CheckCircle2, Loader2, Plus, ArrowRightLeft, Building2,
} from "lucide-react";

const MESES = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const periodLabel = (p) => {
  const [y, m] = (p || "").split("-");
  return m ? `${MESES[parseInt(m, 10)]} ${y}` : p;
};

export default function SendToProjects() {
  const { currentWaveId } = useWaves();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [editingLink, setEditingLink] = useState(false);
  const [mode, setMode] = useState("existing");
  const [selProject, setSelProject] = useState("");
  const [newRef, setNewRef] = useState("");
  const [newName, setNewName] = useState("");
  const [selRest, setSelRest] = useState(() => new Set());
  const [selPer, setSelPer] = useState(() => new Set());
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/export/status");
      setStatus(data);
      setEditingLink(!data.project);
    } catch (e) {
      toast.error("No se pudo cargar el estado de exportación");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get("/export/projects");
      setProjects(data.projects || []);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { load(); loadProjects(); }, [load, loadProjects, currentWaveId]);

  const linkProject = async () => {
    if (mode === "existing" && !selProject) return toast.error("Elige un proyecto");
    if (mode === "new" && !newName.trim() && !newRef.trim()) return toast.error("Escribe la referencia o el nombre");
    setBusy("link");
    try {
      const body = mode === "existing"
        ? { mode: "existing", project_id: selProject }
        : { mode: "new", reference: newRef.trim(), name: newName.trim() };
      await api.post("/export/link", body);
      toast.success("Proyecto vinculado");
      setEditingLink(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
    } finally { setBusy(""); }
  };

  const toggle = (setFn, val) => setFn((prev) => {
    const n = new Set(prev);
    n.has(val) ? n.delete(val) : n.add(val);
    return n;
  });

  const sendRestaurants = async () => {
    if (selRest.size === 0) return toast.error("Selecciona al menos un restaurante");
    setBusy("rest");
    try {
      const { data } = await api.post("/export/send", { restaurant_ids: Array.from(selRest) });
      toast.success(`${data.created} gastos enviados a Proyectos`);
      setSelRest(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    } finally { setBusy(""); }
  };

  const sendPersonal = async () => {
    if (selPer.size === 0) return toast.error("Selecciona al menos un mes");
    setBusy("per");
    try {
      const { data } = await api.post("/export/send", { send_personal: true, personal_periods: Array.from(selPer) });
      toast.success(`${data.created} nóminas enviadas a Proyectos`);
      setSelPer(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    } finally { setBusy(""); }
  };

  if (loading && !status) {
    return <div className="flex items-center gap-2 text-sm text-zinc-500 py-16 justify-center" data-testid="dining-export-loading"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  }

  const rests = status?.restaurants || [];
  const pers = status?.personal || [];
  const linked = status?.project;

  return (
    <div className="space-y-6" data-testid="dining-export-page">
      <div>
        <p className="label-eyebrow">Integración</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Enviar a Proyectos</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Vuelca los <strong>gastos</strong> de esta wave al proyecto del CRM (y de ahí al Cashflow): pagos a restaurantes y nóminas de personal.
          Envíalo por partes según lo vayas cerrando; reenviar actualiza sin duplicar. Los <strong>ingresos</strong> se meten a mano en la pestaña de Ingresos del proyecto.
        </p>
      </div>

      {/* Proyecto vinculado */}
      <section className="border border-border bg-card p-5" data-testid="export-project-card">
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="h-4 w-4 text-zinc-500" />
          <h2 className="font-heading text-lg">Proyecto destino</h2>
        </div>

        {linked && !editingLink ? (
          <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="export-linked-info">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-none border border-border flex items-center justify-center bg-zinc-50">
                <Building2 className="h-5 w-5 text-zinc-500" />
              </div>
              <div>
                <p className="font-mono text-xs text-zinc-500">{linked.reference || "sin referencia"}</p>
                <p className="font-medium">{linked.name}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setEditingLink(true); loadProjects(); }} data-testid="change-link-btn">
              <ArrowRightLeft className="h-4 w-4 mr-1" /> Cambiar
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setMode("existing")}
                className={`pill ${mode === "existing" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white"}`}
                data-testid="link-mode-existing">Vincular existente</button>
              <button
                onClick={() => setMode("new")}
                className={`pill ${mode === "new" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white"}`}
                data-testid="link-mode-new">Crear nuevo</button>
            </div>

            {mode === "existing" ? (
              <select
                value={selProject}
                onChange={(e) => setSelProject(e.target.value)}
                className="w-full max-w-xl border border-border bg-white px-3 py-2 text-sm rounded-none"
                data-testid="export-project-select">
                <option value="">— Elige un proyecto activo —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.reference ? `${p.reference} · ` : ""}{p.name}</option>
                ))}
              </select>
            ) : (
              <div className="grid gap-3 sm:grid-cols-[160px_1fr] max-w-xl">
                <Input placeholder="Referencia (05XX)" value={newRef} onChange={(e) => setNewRef(e.target.value)} className="rounded-none" data-testid="export-new-ref" />
                <Input placeholder="Nombre (p.ej. Dinning Wave 2 2026)" value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-none" data-testid="export-new-name" />
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={linkProject} disabled={busy === "link"} data-testid="export-link-btn">
                {busy === "link" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                {mode === "existing" ? "Vincular" : "Crear y vincular"}
              </Button>
              {linked && (
                <Button variant="ghost" onClick={() => setEditingLink(false)} data-testid="export-link-cancel">Cancelar</Button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Bloque restaurantes */}
      <section className="border border-border bg-card" data-testid="export-restaurants-block">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-border flex-wrap">
          <div className="flex items-center gap-2">
            <Store className="h-4 w-4 text-zinc-500" />
            <h2 className="font-heading text-lg">Gastos · Pagos a restaurantes</h2>
          </div>
          <Button size="sm" onClick={sendRestaurants} disabled={!linked || busy === "rest" || selRest.size === 0} data-testid="export-send-restaurants-btn">
            {busy === "rest" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Enviar seleccionados ({selRest.size})
          </Button>
        </div>
        {rests.length === 0 ? (
          <p className="text-sm text-zinc-500 p-6">No hay restaurantes con facturas en esta wave.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-border">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={selRest.size === rests.length && rests.length > 0}
                    onCheckedChange={(c) => setSelRest(c ? new Set(rests.map((r) => r.id)) : new Set())}
                    data-testid="export-rest-select-all" />
                </th>
                <th className="p-3">Restaurante</th>
                <th className="p-3">Ciudad</th>
                <th className="p-3 text-center">IVA</th>
                <th className="p-3 text-center">Facturas</th>
                <th className="p-3 text-right">Total (IVA incl.)</th>
                <th className="p-3 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {rests.map((r) => (
                <tr key={r.id} className="border-b border-border/60 hover:bg-zinc-50">
                  <td className="p-3">
                    <Checkbox checked={selRest.has(r.id)} onCheckedChange={() => toggle(setSelRest, r.id)} data-testid={`export-rest-${r.id}`} />
                  </td>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 text-zinc-500">{r.city || "—"}</td>
                  <td className="p-3 text-center font-mono text-xs">{r.iva_percent}%</td>
                  <td className="p-3 text-center font-mono text-xs">{r.invoice_count}</td>
                  <td className="p-3 text-right font-mono">{formatEUR(r.gross_total)}</td>
                  <td className="p-3 text-center">
                    {r.sent
                      ? <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200" title={`${r.sent_count} gastos`}><CheckCircle2 className="h-3 w-3" /> Enviado</span>
                      : <span className="pill text-zinc-500">Pendiente</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Bloque personal */}
      <section className="border border-border bg-card" data-testid="export-personal-block">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-border flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-zinc-500" />
            <h2 className="font-heading text-lg">Gastos · Personal catering</h2>
          </div>
          <Button size="sm" onClick={sendPersonal} disabled={!linked || busy === "per" || selPer.size === 0} data-testid="export-send-personal-btn">
            {busy === "per" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Enviar seleccionados ({selPer.size})
          </Button>
        </div>
        {pers.length === 0 ? (
          <p className="text-sm text-zinc-500 p-6">No hay nóminas calculadas en esta wave.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-border">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={selPer.size === pers.length && pers.length > 0}
                    onCheckedChange={(c) => setSelPer(c ? new Set(pers.map((p) => p.period)) : new Set())}
                    data-testid="export-personal-select-all" />
                </th>
                <th className="p-3">Mes</th>
                <th className="p-3 text-center">Empleados</th>
                <th className="p-3 text-right">Total (sin IVA)</th>
                <th className="p-3 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {pers.map((p) => (
                <tr key={p.period} className="border-b border-border/60 hover:bg-zinc-50">
                  <td className="p-3">
                    <Checkbox checked={selPer.has(p.period)} onCheckedChange={() => toggle(setSelPer, p.period)} data-testid={`export-personal-${p.period}`} />
                  </td>
                  <td className="p-3 font-medium">{periodLabel(p.period)}</td>
                  <td className="p-3 text-center font-mono text-xs">{p.employee_count}</td>
                  <td className="p-3 text-right font-mono">{formatEUR(p.gross_total)}</td>
                  <td className="p-3 text-center">
                    {p.sent
                      ? <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3" /> Enviado</span>
                      : <span className="pill text-zinc-500">Pendiente</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
